// Inline Competitor Intel badge (RC-460, 2026-02-28)
//
// Rendered inside CreateQuotationModal / order builder, once per
// line item. Fetches /api/competitor-prices/lookup for the given
// jp_part_no + customer_id and shows a single-line summary with the
// cheapest competitor price + gap % vs our price.
//
// Access-gated server-side to the two Price Master admins. For
// anyone else the backend returns 403 and this component silently
// renders nothing — zero noise for non-Price-Master admins.

import React, { useState, useEffect, memo } from 'react';
import axios from 'axios';
import { TrendingDown, TrendingUp, Minus as MinusIcon } from 'lucide-react';
import { Badge } from './ui/badge';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from './ui/tooltip';

const API = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ timeout: 10000 });

// 2026-02-28 · Session-level cache so a part/customer pair is only
// fetched ONCE per modal open — kills a potential 50+ req flood if
// the admin builds a 50-line quotation. We store the PROMISE (not
// just the resolved value) so two concurrent mounts for the same
// line item share one HTTP call instead of racing. Call
// `invalidateCompetitorIntelCache()` after any create/update/delete
// in the admin panel to force a re-fetch on next badge mount.
const LOOKUP_CACHE = new Map();

export function invalidateCompetitorIntelCache() {
  LOOKUP_CACHE.clear();
}


const CompetitorIntelBadge = memo(function CompetitorIntelBadge({
  jpPartNo,
  customerId,
  ourPrice,
  token,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (!jpPartNo || !token) return;
    const key = `${jpPartNo}:${customerId || 'none'}:${ourPrice || 0}`;
    let cancelled = false;

    // De-dupe concurrent mounts: if a lookup for this key is
    // already in-flight (promise cached), reuse it instead of
    // firing a second HTTP request.
    const cached = LOOKUP_CACHE.get(key);
    if (cached && typeof cached.then === 'function') {
      cached.then((val) => { if (!cancelled) { setData(val); setLoading(false); } })
            .catch(() => { if (!cancelled) setLoading(false); });
      return () => { cancelled = true; };
    }
    if (cached) {
      // Resolved value cached
      setData(cached);
      setLoading(false);
      return;
    }

    const promise = api.get(`${API}/api/competitor-prices/lookup`, {
      params: {
        jp_part_no: jpPartNo,
        customer_id: customerId || undefined,
        our_price: ourPrice || undefined,
      },
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => {
      LOOKUP_CACHE.set(key, r.data);
      return r.data;
    });
    LOOKUP_CACHE.set(key, promise);

    promise.then((val) => { if (!cancelled) { setData(val); setLoading(false); } })
           .catch((e) => {
             LOOKUP_CACHE.delete(key);
             if (!cancelled) {
               if (e.response?.status === 403) setForbidden(true);
               setLoading(false);
             }
           });
    return () => { cancelled = true; };
  }, [jpPartNo, customerId, ourPrice, token]);

  if (forbidden) return null;                          // non-admin: hide
  if (loading) return null;                            // avoid flicker
  if (!data || !data.has_data) return null;            // no intel yet
  if (data.cheapest_competitor_overall == null) return null;

  const cheapest = Number(data.cheapest_competitor_overall);
  const gap = data.gap_pct;  // positive = we're ABOVE cheapest competitor

  // Choose color + icon based on gap. Use static classes (not the
  // shadcn Badge default variant) so hover doesn't flip to the
  // primary red solid, which murders contrast.
  let tone = 'bg-gray-100 text-gray-700 border-gray-300';
  let Icon = MinusIcon;
  let tooltip = 'Competitor pricing on file';
  if (gap != null) {
    if (gap > 5) {
      tone = 'bg-red-50 text-red-700 border-red-300';
      Icon = TrendingUp;
      tooltip = `You are ${gap.toFixed(1)}% ABOVE the cheapest competitor — consider matching`;
    } else if (gap < -5) {
      tone = 'bg-emerald-50 text-emerald-700 border-emerald-300';
      Icon = TrendingDown;
      tooltip = `You are ${Math.abs(gap).toFixed(1)}% BELOW the cheapest competitor — strong position`;
    } else {
      tone = 'bg-amber-50 text-amber-700 border-amber-300';
      tooltip = 'Within 5% of cheapest competitor';
    }
  }

  // Build the detail tooltip — top customer-specific first, then market
  const entries = [
    ...(data.customer_specific || []).map(r => ({ ...r, bucket: 'For this customer' })),
    ...(data.general_market || []).map(r => ({ ...r, bucket: 'Market' })),
  ].slice(0, 5);

  return (
    <TooltipProvider>
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <div
            data-testid={`comp-intel-badge-${jpPartNo}`}
            className="inline-flex items-center gap-1 mt-1 cursor-help"
          >
            <span
              className={`text-[10px] px-2 py-0.5 rounded-full border inline-flex items-center gap-1 font-medium ${tone}`}
            >
              <Icon className="w-3 h-3" />
              Competitor: ₹{cheapest.toLocaleString('en-IN')}
              {gap != null && (
                <span className="ml-0.5">
                  ({gap > 0 ? '+' : ''}{gap.toFixed(1)}%)
                </span>
              )}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-sm">
          <div className="text-xs space-y-1">
            <div className="font-semibold">{tooltip}</div>
            <div className="mt-2 space-y-1">
              {entries.map((e, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-3"
                  data-testid={`comp-intel-entry-${jpPartNo}-${i}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {e.competitor_name}
                      <span className="ml-1 uppercase text-[9px] opacity-70">
                        · {e.quality_tier}
                      </span>
                    </div>
                    <div className="opacity-70 text-[10px]">
                      {e.bucket}{e.observed_date ? ` · ${e.observed_date}` : ''}
                    </div>
                  </div>
                  <div className="font-semibold whitespace-nowrap">
                    ₹{Number(e.price).toLocaleString('en-IN')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

export default CompetitorIntelBadge;
